import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';

// 11 plates uploaded by the founder for the hero ring (cinematic interior /
// editorial / fashion mood-board imagery — used in /public/hero-ring/).
const RING_PLATES = [
  { src: '/hero-ring/plate-1.jpg',  tag: 'Studio Amaka' },
  { src: '/hero-ring/plate-2.jpg',  tag: 'Furniture' },
  { src: '/hero-ring/plate-3.jpg',  tag: 'Reels Course' },
  { src: '/hero-ring/plate-4.jpg',  tag: 'Modern Rustic' },
  { src: '/hero-ring/plate-5.jpg',  tag: 'Architecture' },
  { src: '/hero-ring/plate-6.jpg',  tag: 'Editorial 47' },
  { src: '/hero-ring/plate-7.jpg',  tag: 'Editorial 48' },
  { src: '/hero-ring/plate-8.jpg',  tag: 'Editorial 49' },
  { src: '/hero-ring/plate-9.jpg',  tag: 'Editorial 50' },
  { src: '/hero-ring/plate-10.jpg', tag: 'Rich Content' },
  { src: '/hero-ring/plate-11.jpg', tag: 'Wildberries' },
];

const HeroV2 = () => {
  const { t } = useTranslation();
  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });

  // Headline: each line in its own overflow:hidden container, slid up in sequence.
  const lineReveal = {
    hidden: { y: '110%' },
    visible: (i) => ({ y: '0%', transition: { duration: 0.95, delay: 0.18 * i, ease: [0.22, 1, 0.36, 1] } }),
  };

  const N = RING_PLATES.length;

  return (
    <section data-testid="hero-section" className="relative overflow-hidden bg-[#FAF8F5] pt-2 min-h-[calc(100vh-72px)] lg:h-[calc(100vh-80px)] lg:min-h-[640px]">
      {/* Noise */}
      <div className="absolute inset-0 noise-overlay pointer-events-none z-[1]" />

      {/* Animated background marquee */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <motion.div className="absolute whitespace-nowrap select-none" style={{ top: '6%' }}
          animate={{ x: ['5%', '-50%'] }} transition={{ duration: 28, repeat: Infinity, ease: 'linear' }}>
          <span className="text-[20vw] font-bold text-[#1A1816]/[0.025] tracking-[-0.05em] font-sans leading-none">
            HORIZON&nbsp;&nbsp;DIGITAL&nbsp;&nbsp;AGENCY&nbsp;&nbsp;HORIZON&nbsp;&nbsp;DIGITAL
          </span>
        </motion.div>
      </div>

      <div className="relative z-10 max-w-[1500px] mx-auto px-6 lg:px-12 pt-3 sm:pt-4 lg:pt-6 pb-8 h-full flex items-center">
        <div className="grid lg:grid-cols-[1fr_1.05fr] items-center gap-10 lg:gap-12">

          {/* LEFT — copy block */}
          <div className="order-2 lg:order-1">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.1 }}>
              <div className="flex items-center gap-3 mb-6">
                <motion.div className="h-[1px] bg-[#C9A96E]" initial={{ width: 0 }} animate={{ width: 48 }} transition={{ duration: 0.8, delay: 0.3 }} />
                <span className="text-[10px] sm:text-[10px] lg:text-[9px] font-semibold tracking-[0.32em] uppercase text-[#8A857D]">{t('hero.eyebrow')}</span>
              </div>
            </motion.div>

            <h1 className="sr-only">Horizon Digital Agency — premium ready-to-launch website templates &amp; bespoke design</h1>
            <div aria-hidden="true">
              <div className="overflow-hidden"><motion.div className="hero-headline font-sans font-bold text-[#1A1816] tracking-[-0.03em] leading-[0.95]" custom={0} initial="hidden" animate="visible" variants={lineReveal}>{t('common.headline_we')} <span className="font-serif italic text-[#C9A96E]">{t('common.headline_design')}</span> {t('common.headline_rebuild')}</motion.div></div>
              <div className="overflow-hidden"><motion.div className="hero-headline font-sans font-bold text-[#1A1816] tracking-[-0.03em] leading-[0.95]" custom={1} initial="hidden" animate="visible" variants={lineReveal}>{t('common.headline_and')} <span className="font-serif italic text-[#C9A96E]">{t('common.headline_optimize')}</span> {t('common.headline_websites')}</motion.div></div>
              <div className="overflow-hidden"><motion.div className="hero-headline font-sans font-bold text-[#1A1816] tracking-[-0.03em] leading-[0.95]" custom={2} initial="hidden" animate="visible" variants={lineReveal}>{t('common.headline_for')}</motion.div></div>
            </div>

            <motion.p className="mt-6 text-base sm:text-base lg:text-base text-[#8A857D] max-w-md leading-relaxed" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }}>{t('hero.sub')}</motion.p>

            <motion.div className="mt-8 flex flex-wrap gap-4 items-center" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.85 }}>
              <button data-testid="hero-cta-templates" onClick={() => { window.location.href = '/templates'; }} className="magnetic-btn !py-3 !px-7 sm:!py-3.5 sm:!px-8 !text-[11px] sm:!text-[10px]">
                <span>{t('common.browse_templates')}</span>
              </button>
              <button data-testid="hero-cta-contact" onClick={() => scrollTo('#contact')} className="group inline-flex items-center gap-2 text-sm sm:text-sm font-semibold text-[#1A1816]/60 hover:text-[#C9A96E] transition-colors tracking-wide uppercase">
                {t('hero.cta2')} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>

            <motion.div className="mt-12 sm:mt-14 grid grid-cols-3 gap-6 max-w-md" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.1 }}>
              {[['28', 'Premium templates'], ['$850+', 'Per template'], ['12+', t('stats.countries')]].map(([num, label]) => (
                <div key={num}>
                  <p className="text-3xl sm:text-3xl font-serif italic text-[#C9A96E]">{num}</p>
                  <p className="text-[11px] sm:text-[10px] text-[#8A857D] mt-1 tracking-wide">{label}</p>
                </div>
              ))}
            </motion.div>
          </div>

          {/* RIGHT — 3D rotating ring of mood-board plates */}
          <div className="order-1 lg:order-2 w-full">
            <motion.div
              data-testid="hero-ring-stage"
              className="ring-stage relative mx-auto"
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            >
              {/* Side fade masks for cinematic edge falloff */}
              <div className="ring-edge-fade pointer-events-none" aria-hidden="true" />

              <div className="ring-rotator" aria-hidden="true">
                {RING_PLATES.map((p, i) => {
                  const angle = (360 / N) * i;
                  return (
                    <motion.figure
                      key={p.src}
                      data-testid={`ring-card-${i + 1}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 1.0, delay: 0.45 + i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                      className="ring-card"
                      style={{ transform: `rotateY(${angle}deg) translateZ(var(--ring-radius))` }}
                    >
                      <img src={p.src} alt={p.tag} className="w-full h-full object-cover" loading="eager" decoding="async" draggable="false" />
                      <figcaption className="ring-card-caption">
                        <span>— {p.tag}</span>
                      </figcaption>
                    </motion.figure>
                  );
                })}
              </div>

              {/* Soft floor reflection / vignette ring */}
              <div className="ring-floor pointer-events-none" aria-hidden="true" />
            </motion.div>

            {/* Caption strip below the ring */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 0.8 }} className="mt-6 flex items-center justify-between font-mono text-[10px] sm:text-[9px] tracking-[0.32em] uppercase text-[#8A857D]">
              <span>— {t('common.eleven_plates')}</span>
              <a href="/templates" data-testid="hero-collage-link" className="text-[#1A1816] border-b border-[#1A1816]/30 hover:border-[#C9A96E] hover:text-[#C9A96E] pb-0.5 transition-colors">{t('common.open_catalogue')}</a>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroV2;
